import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, IconButton, Box, Typography,
  CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, TablePagination, Collapse, Tooltip, TableSortLabel, Button, Menu, MenuItem
} from '@mui/material';
import { AiOutlineClose } from 'react-icons/ai';
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';
import { PiFileCsv } from 'react-icons/pi';
import { useQuery } from '@tanstack/react-query';
import { fetchLocationActivityDetail } from '../../api/dashboardApi';

const Row = ({ row, status, onOpenPdf }) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuUrl, setMenuUrl] = useState(null);
  const openMenu = Boolean(anchorEl);

  const handleMenuClick = (event, url) => {
    event.stopPropagation();
    setMenuUrl(url);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (event) => {
    if (event && event.stopPropagation) event.stopPropagation();
    setAnchorEl(null);
  };

  const handleOpenDialog = (event) => {
    event.stopPropagation();
    handleMenuClose();
    if (menuUrl && onOpenPdf) onOpenPdf(menuUrl);
  };

  const handleOpenWindow = (event) => {
    event.stopPropagation();
    handleMenuClose();
    if (menuUrl) window.open(menuUrl, '_blank', 'width=1000,height=800,scrollbars=yes,resizable=yes');
  };

  return (
    <React.Fragment>
      <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>
          {row.location_name && row.location_id ? (
            <Tooltip title={row.location_name} arrow placement="top">
              <span style={{ cursor: "help", borderBottom: "1px dashed #9ca3af" }}>
                {row.location_id}
              </span>
            </Tooltip>
          ) : (
            row.location_id || "-"
          )}
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{row.months_inactive}</TableCell>
        <TableCell>{row.last_billed_month || "-"}</TableCell>
        <TableCell>
          {row.last_billed_invoice_ids && row.last_billed_invoice_ids.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {row.last_billed_invoice_ids.map((id, idx) => {
                const invData = (row.last_billed_invoices || row.invoices || []).find(inv => inv.invoice_id === id);
                return (
                  <React.Fragment key={idx}>
                    {invData && invData.invoice_pdf_url ? (
                      <span
                        onClick={(e) => handleMenuClick(e, invData.invoice_pdf_url)}
                        style={{ cursor: 'pointer', color: '#0ea5e9', fontWeight: 600, textDecoration: 'underline' }}
                      >
                        {id}
                      </span>
                    ) : (
                      <span>{id}</span>
                    )}
                    {idx < row.last_billed_invoice_ids.length - 1 && <span>, </span>}
                  </React.Fragment>
                );
              })}
            </Box>
          ) : (
            "-"
          )}
        </TableCell>
        <TableCell align="right">
          {row.last_billed_amount_usd != null 
            ? `$${Number(row.last_billed_amount_usd).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
            : "-"}
        </TableCell>
        <TableCell>{row.country || "-"}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2, p: 2, backgroundColor: '#f1f5f9', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom component="div" sx={{ fontWeight: 'bold', color: '#475569' }}>
                Invoice History ({row.invoices?.length || row.invoice_ids?.length || 0} invoices)
              </Typography>
              {row.invoices && row.invoices.length > 0 ? (
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: '#475569', textTransform: 'uppercase', bgcolor: '#e2e8f0' }}>Invoice Date</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: '#475569', textTransform: 'uppercase', bgcolor: '#e2e8f0' }}>Invoice ID</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: '#475569', textTransform: 'uppercase', bgcolor: '#e2e8f0' }}>PDF</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {row.invoices.map((inv, idx) => {
                        const d = inv.invoice_date ? new Date(inv.invoice_date) : null;
                        const monthLabel = d ? d.toLocaleString('default', { month: 'long', year: 'numeric' }) : '-';
                        const dateLabel = d ? `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()}` : '-';
                        return (
                          <TableRow key={idx} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                            <TableCell sx={{ fontSize: '12px', color: '#334155' }}>{dateLabel}</TableCell>
                            <TableCell sx={{ fontSize: '12px', fontWeight: 600, color: '#7C2EA0' }}>{inv.invoice_id}</TableCell>
                            <TableCell>
                              {inv.invoice_pdf_url ? (
                                <>
                                  <Button
                                    variant="text"
                                    size="small"
                                    onClick={(e) => handleMenuClick(e, inv.invoice_pdf_url)}
                                    sx={{ textTransform: 'none', color: '#0ea5e9', fontSize: '12px', fontWeight: 600, minWidth: 0, p: 0 }}
                                  >
                                    View PDF
                                  </Button>

                                </>
                              ) : '-'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : row.invoice_ids && row.invoice_ids.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {row.invoice_ids.map((invId, idx) => (
                    <Paper key={idx} sx={{ px: 1.5, py: 0.5, backgroundColor: '#fff', border: '1px solid #cbd5e1', fontSize: '0.875rem' }} elevation={0}>
                      {invId}
                    </Paper>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary">No invoice history available.</Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
      <Menu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleOpenDialog} sx={{ fontSize: '13px' }}>Open in Dialog</MenuItem>
        <MenuItem onClick={handleOpenWindow} sx={{ fontSize: '13px' }}>Open in New Window</MenuItem>
      </Menu>
    </React.Fragment>
  );
};

const descendingComparator = (a, b, orderBy) => {
  let valA = a[orderBy];
  let valB = b[orderBy];
  
  if (valA === null || valA === undefined) valA = "";
  if (valB === null || valB === undefined) valB = "";

  if (valB < valA) return -1;
  if (valB > valA) return 1;
  return 0;
};

const getComparator = (order, orderBy) => {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

const LocationActivityDetailModal = ({ open, onClose, months, status }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('months_inactive');
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState('');

  const handleOpenPdfDialog = (url) => {
    setCurrentPdfUrl(url);
    setPdfDialogOpen(true);
  };

  const handleClosePdfDialog = () => {
    setPdfDialogOpen(false);
  };

  useEffect(() => {
    if (open) {
      setPage(0);
    }
  }, [open, months, status]);

  const { data: detailData, isLoading, isError } = useQuery({
    queryKey: ['locationActivityDetail', months, status],
    queryFn: () => fetchLocationActivityDetail(months, status),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const locations = detailData?.data?.locations || [];
  const totalCount = detailData?.data?.count || 0;

  const handleDownloadCSV = () => {
    if (!locations || locations.length === 0) return;
    try {
      const headers = [
        "Location ID",
        "Location Name",
        "Months Inactive",
        "Last Billed Month",
        "Last Invoice IDs",
        "Last Billed Amount ($)",
        "Country"
      ];
      const csvRows = [headers.join(",")];

      locations.forEach(row => {
        const values = [
          `"${row.location_id || ""}"`,
          `"${row.location_name || ""}"`,
          row.months_inactive || 0,
          `"${row.last_billed_month || ""}"`,
          `"${(row.last_billed_invoice_ids || []).join(" | ")}"`,
          row.last_billed_amount_usd != null ? row.last_billed_amount_usd : "",
          `"${row.country || ""}"`
        ];
        csvRows.push(values.join(","));
      });

      const csvString = csvRows.join("\n");
      const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      const fileName = `${status === 'inactive' ? 'Idle' : 'Active'}_Location_IDs_${months}M_${new Date().toISOString().split('T')[0]}.csv`;
      link.setAttribute("download", fileName);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("CSV Download Error:", err);
    }
  };

  const sortedLocations = [...locations].sort(getComparator(order, orderBy));
  const paginatedLocations = sortedLocations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const createSortHandler = (property) => () => handleRequestSort(property);

  const renderSortableHeader = (label, property, align = "left") => (
    <TableCell align={align} sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700 }} sortDirection={orderBy === property ? order : false}>
      <TableSortLabel
        active={orderBy === property}
        direction={orderBy === property ? order : 'asc'}
        onClick={createSortHandler(property)}
        sx={{
          color: '#fff !important',
          '& .MuiTableSortLabel-icon': { color: '#fff !important' },
          '&:hover': { color: '#e0e0e0 !important' }
        }}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth PaperProps={{ sx: { borderRadius: 2, minHeight: "70vh", maxHeight: "90vh" } }}>
      <DialogTitle sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e0e0e0" }}>
        <Box>
          <Typography variant="h6" fontWeight={600} sx={{ color: "#7C2EA0" }}>
            {status === 'inactive' ? 'Idle Location IDs' : 'Active Location IDs'} Detail
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {totalCount} locations (Threshold: {status === 'inactive' ? '≥' : '<'} {months} months)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Tooltip title="Download CSV" placement="top" arrow>
            <span
              onClick={locations && locations.length > 0 ? handleDownloadCSV : undefined}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "50%",
                width: "32px",
                height: "32px",
                backgroundColor: "#f5f5f5",
                cursor: (locations && locations.length > 0) ? "pointer" : "default",
                opacity: (locations && locations.length > 0) ? 1 : 0.5,
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                if (locations && locations.length > 0) {
                  e.currentTarget.style.backgroundColor = "#e0e0e0";
                }
              }}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
            >
              <PiFileCsv size={18} color="#333" />
            </span>
          </Tooltip>
          <IconButton onClick={onClose} sx={{ color: "#9e9e9e" }}><AiOutlineClose /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, backgroundColor: "#f8f9fa", display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 3, flexGrow: 1, overflow: 'auto' }}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
              <CircularProgress sx={{ color: "#7C2EA0" }} />
            </Box>
          ) : isError ? (
            <Box sx={{ p: 4, textAlign: "center", color: "#EF4444" }}>Failed to load location activity detail.</Box>
          ) : locations.length > 0 ? (
            <Paper elevation={0} sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}>
              <TableContainer sx={{ maxHeight: "55vh" }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ backgroundColor: "#7C2EA0", width: 50 }} />
                      {renderSortableHeader("Location ID", "location_id")}
                      {renderSortableHeader("Months Inactive", "months_inactive", "right")}
                      {renderSortableHeader("Last Billed Month", "last_billed_month")}
                      <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700 }}>Last Invoice IDs</TableCell>
                      {renderSortableHeader("Last Billed Amount ($)", "last_billed_amount_usd", "right")}
                      {renderSortableHeader("Country", "country")}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedLocations.map((row, index) => (
                      <Row key={`${row.location_id}-${index}`} row={row} status={status} onOpenPdf={handleOpenPdfDialog} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={locations.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Paper>
          ) : (
            <Box sx={{ p: 4, textAlign: "center", color: "#6b7280" }}>No locations found.</Box>
          )}
        </Box>
      </DialogContent>
      <Dialog open={pdfDialogOpen} onClose={handleClosePdfDialog} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
          <Typography variant="h6" fontSize="16px" fontWeight="bold">Invoice Preview</Typography>
          <Button onClick={handleClosePdfDialog} color="inherit" size="small">Close</Button>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '80vh', p: 0 }}>
          {currentPdfUrl && (
            <iframe
              src={currentPdfUrl}
              title="PDF Preview"
              width="100%"
              height="100%"
              style={{ border: 'none' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default LocationActivityDetailModal;
